import axios from 'axios';
import { ApiService } from './apiService';
import { LightspeedErrorHandler, LightspeedErrorCodes, handleLightspeedError } from '../shared/utils/lightspeedErrorHandler';
import {
  LightspeedApiResponse,
  LightspeedOAuthSession,
  LightspeedConnection,
  LightspeedConnectionStatus,
  LightspeedInstallation,
  LightspeedOAuthCallback,
  LightspeedConnectionStatusPayload
} from '../shared/types/lightspeed.types';

import { STORE_BASE as API_BASE } from '../services/paths';

export interface OAuthStartResponse {
  success: boolean;
  data: {
    authorizationUrl: string;
    sessionId: string;
    type: 'connect' | 'reconnect';
    expiresAt: string;
  };
  error?: string;
}

export interface ConnectionStatusResponse {
  success: boolean;
  data: {
    isConnected: boolean;
    storeName?: string;
    accountId?: string;
    lastSynced?: string;
  };
  error?: string;
}

export interface InstallationStatusResponse {
  success: boolean;
  data: {
    status: 'pending' | 'completed' | 'failed' | 'expired';
    sessionId: string;
    authorizationUrl?: string;
    storeId?: string;
    error?: string;
  };
  error?: string;
}

class LightspeedService {
  async startOAuth(storeId?: string, userId?: string, domainPrefix?: string): Promise<LightspeedApiResponse<LightspeedOAuthSession>> {
    try {
      const queryParams = new URLSearchParams();
      if (storeId) {
        queryParams.append('StoreId', storeId);
      }
      if (userId) {
        queryParams.append('UserId', userId);
      } 
      if (domainPrefix) {
        queryParams.append('DomainPrefix', domainPrefix);
      } 
      
      const queryString = queryParams.toString();
      const url = queryString ? `${API_BASE}/OAuth/start?${queryString}` : `${API_BASE}/OAuth/start`;
      
      const response = await ApiService.request({
        url: url,
        http_method: 'GET'
      });

      // Respect server wrapper { success, message, error, data }
      const raw = (response as any)?.data;
      const hasWrapper = raw && typeof raw === 'object';
      const successFlag = hasWrapper && 'success' in raw ? Boolean((raw as any).success) : true;
      const errorMessage = hasWrapper && 'error' in raw ? String((raw as any).error) : undefined;
      const normalized = hasWrapper && 'data' in raw ? (raw as any).data : raw;

      if (!successFlag) {
        // Map known OAuth start errors to Lightspeed errors when possible
        // If the backend ever returns session-related errors here, handle similarly to callback
        if (errorMessage && /invalid|expired session/i.test(errorMessage)) {
          throw LightspeedErrorHandler.createError(LightspeedErrorCodes.OAUTH_INVALID_STATE, { message: errorMessage });
        }
        throw new Error(errorMessage || 'Failed to start OAuth flow');
      }

      return {
        success: true,
        data: normalized
      };
    } catch (error) {
      throw handleLightspeedError(error);
    }
  }

  async handleCallback(callbackData: LightspeedOAuthCallback): Promise<LightspeedApiResponse<LightspeedConnection>> {
    try {
      if (callbackData.error) {
        throw LightspeedErrorHandler.mapOAuthErrorToLightspeedError(
          callbackData.error,
          callbackData.errorDescription
        );
      }

      if (!callbackData.code) {
        throw LightspeedErrorHandler.createError(LightspeedErrorCodes.OAUTH_INVALID_CODE);
      }

      if (!callbackData.state) {
        throw LightspeedErrorHandler.createError(LightspeedErrorCodes.OAUTH_INVALID_STATE);
      }

      const requestData = {
        code: callbackData.code,
        state: callbackData.state,
        sessionId: callbackData.sessionId
      };

      const response = await ApiService.request({
        url: `${API_BASE}/OAuth/callback`,
        http_method: 'POST',
        data: requestData
      });
      
      // Respect server wrapper { success, message, error, data }
      const raw = (response as any)?.data;
      const hasWrapper = raw && typeof raw === 'object';
      const successFlag = hasWrapper && 'success' in raw ? Boolean((raw as any).success) : true;
      const errorMessage = hasWrapper && 'error' in raw ? String((raw as any).error) : undefined;
      const normalized = hasWrapper && 'data' in raw ? (raw as any).data : raw;

      if (!successFlag) {
        // Map specific session error for clearer UX
        if (errorMessage && /invalid|expired session/i.test(errorMessage)) {
          throw LightspeedErrorHandler.createError(LightspeedErrorCodes.OAUTH_INVALID_STATE, { message: errorMessage });
        }
        throw new Error(errorMessage || 'Lightspeed callback failed');
      }

      return {
        success: true,
        data: normalized
      };
    } catch (error) {
      throw handleLightspeedError(error);
    }
  }

  async checkConnectionStatus(storeId: string): Promise<LightspeedApiResponse<LightspeedConnectionStatusPayload | null>> {
    try {
      const response = await ApiService.request({
        url: `${API_BASE}/OAuth/stores/${storeId}/connection`,
        http_method: 'GET'
      });
      // Normalize wrapped server response: { success, message, error, data }
      const raw = (response as any)?.data;
      const normalized = raw && typeof raw === 'object' && 'data' in raw ? raw.data : raw;
      
      return {
        success: true,
        data: normalized
      };
    } catch (error) {
      throw handleLightspeedError(error);
    }
  }

  async disconnectStore(storeId: string): Promise<LightspeedApiResponse<{ success: boolean }>> {
    try {
      const response = await ApiService.request({
        url: `${API_BASE}/OAuth/stores/${storeId}/connection`,
        http_method: 'DELETE'
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw handleLightspeedError(error);
    }
  }

  async checkInstallationStatus(sessionId: string): Promise<LightspeedApiResponse<LightspeedInstallation>> {
    try {
      const response = await ApiService.request({
        url: `${API_BASE}/OAuth/installations/${sessionId}`,
        http_method: 'GET'
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw handleLightspeedError(error);
    }
  }
}

export const lightspeedService = new LightspeedService();

export default lightspeedService;
